/**
 * Chart overlay toggles (show orders, trade executions, etc.).
 *
 * These are one preference for the whole login, shared by every account.
 * App settings are a single JSON blob, so a later save that started from a
 * stale copy can put a toggle back to its previous value (missing
 * showTradeExecutions is treated as on). The flags here are remembered in
 * this browser and stamped onto every save so that cannot happen.
 */

export const OVERLAY_APP_SETTING_KEYS = [
  'showOrders',
  'showTradeExecutions',
  'showRuleViolations',
  'showAlertsOnChart',
  'showCrosshair',
];

export const OVERLAY_APP_SETTINGS_STORAGE_KEY = 'overlayAppSettings.v1';

let overlayFlags = null;

function readStoredFlags() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(OVERLAY_APP_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const flags = {};
    for (const key of OVERLAY_APP_SETTING_KEYS) {
      if (typeof parsed[key] === 'boolean') flags[key] = parsed[key];
    }
    return Object.keys(flags).length ? flags : null;
  } catch (_) {
    return null;
  }
}

function writeStoredFlags(flags) {
  try {
    if (typeof localStorage === 'undefined' || !flags) return;
    const prev = readStoredFlags() || {};
    const next = { ...prev };
    for (const key of OVERLAY_APP_SETTING_KEYS) {
      if (typeof flags[key] === 'boolean') next[key] = flags[key];
    }
    localStorage.setItem(OVERLAY_APP_SETTINGS_STORAGE_KEY, JSON.stringify(next));
  } catch (_) {}
}

function ensureOverlayFlagsLoaded() {
  if (overlayFlags) return overlayFlags;
  const stored = readStoredFlags();
  if (stored) overlayFlags = { ...stored };
  return overlayFlags;
}

/** Pull the remembered toggles into memory before a settings save. */
export function ensureStoredOverlayFlags() {
  return ensureOverlayFlagsLoaded();
}

export function readStoredOverlayFlag(key) {
  const stored = readStoredFlags();
  if (!stored || typeof stored[key] !== 'boolean') return undefined;
  return stored[key];
}

/** Stored choice wins. Otherwise the in-memory flag. Otherwise the server value. */
export function resolveOverlayFlag(key, serverValue) {
  const stored = readStoredOverlayFlag(key);
  if (typeof stored === 'boolean') return stored;
  ensureOverlayFlagsLoaded();
  if (overlayFlags && typeof overlayFlags[key] === 'boolean') return overlayFlags[key];
  return serverValue;
}

export function setOverlayAppSettings(next) {
  overlayFlags = next ? { ...next } : null;
  if (overlayFlags) writeStoredFlags(overlayFlags);
}

export function clearOverlayAppSettings() {
  overlayFlags = null;
}

export function clearStoredOverlayAppSettings() {
  overlayFlags = null;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(OVERLAY_APP_SETTINGS_STORAGE_KEY);
    }
  } catch (_) {}
}

export function patchOverlayAppSetting(key, value) {
  if (!OVERLAY_APP_SETTING_KEYS.includes(key)) return;
  ensureOverlayFlagsLoaded();
  overlayFlags = { ...(overlayFlags || {}), [key]: value };
  writeStoredFlags(overlayFlags);
}

/** Overlay flags win over the payload so an older full-object write cannot revert them. */
export function applyOverlayAppSettings(appSettings) {
  const next = { ...(appSettings || {}) };
  ensureOverlayFlagsLoaded();
  if (!overlayFlags) return next;
  for (const key of OVERLAY_APP_SETTING_KEYS) {
    if (overlayFlags[key] !== undefined) next[key] = overlayFlags[key];
  }
  return next;
}
